import React, { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { bgGreen, textGreen } from '@osmosis/tailwind-ui/styles';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { LoadingSpinner } from '@osmosis/tailwind-ui/components/LoadingSpinner';
import { Input } from '@osmosis/tailwind-ui/components/Input';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';

import { usePlaylistContext } from '../context/PlaylistProvider';
import { useSnackbarContext } from '../context/SnackbarProvider';

import { PlaylistProps } from '@osmosis/shared/types/playlist';

type Props = {
  toggle: () => void;
  handleAddToPlaylist: (playlist: PlaylistProps) => void;
};

/**
 * Return CreatePlaylistBar component.
 */
const CreatePlaylistBar = ({ toggle, handleAddToPlaylist }: Props) => {
  const [isPlaylistInputVisible, setIsPlaylistInputVisible] = useState(false);
  const [isBtnVisible, setIsBtnVisible] = useState(true);
  const [playlistName, setPlaylistName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const intl = useIntl();
  const { updatePlaylists, createPlaylist: createNewPlaylist } =
    usePlaylistContext();
  const { onAddSnackBar } = useSnackbarContext();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isPlaylistInputVisible) {
      inputRef.current?.focus();
    }
  }, [isPlaylistInputVisible]);

  const createPlaylist = async (e: React.MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    setIsLoading(true);
    const res = await createNewPlaylist({ title: playlistName, onAddSnackBar });

    await handleAddToPlaylist(res as unknown as PlaylistProps);
    await updatePlaylists();

    setIsPlaylistInputVisible(false);
    setIsBtnVisible(true);
    setPlaylistName('');
    setIsLoading(false);
    toggle();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      createPlaylist(e);
    }
  };

  const handleBtnClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsBtnVisible(false);
    setIsPlaylistInputVisible(true);
    inputRef.current?.focus();
  };

  return (
    <Box className="bg-gray-50" data-testid="create-playlist-bar">
      {isBtnVisible && (
        <Button
          className={`w-full bg-gray-50`}
          variant="transparent"
          startIcon={<Icon icon={faPlus} />}
          onClick={handleBtnClick}
          data-testid="create-btn"
        >
          <FormattedMessage
            defaultMessage="Create new"
            id="create_playlist_bar.create_new"
          />
        </Button>
      )}

      {isPlaylistInputVisible && (
        <Flex className={`p-2 items-center justify-between`}>
          {isLoading ? (
            <Flex className="w-full justify-center items-center">
              <LoadingSpinner cn={`${textGreen} text-base`} />
            </Flex>
          ) : (
            <Input
              ref={inputRef}
              value={playlistName}
              onChange={({ target }) => setPlaylistName(target.value)}
              className="outline-none h-[10px] w-[86%] p-3 bg-gray-50"
              placeholder={intl.formatMessage({
                defaultMessage: 'Playlist name',
                id: 'create_playlist_bar.playlist_name',
              })}
              data-testid="create-input"
              onKeyDown={handleKeyDown}
            />
          )}

          <IconButton
            className={`${bgGreen} text-white text-base`}
            onClick={createPlaylist}
            disabled={isLoading}
            icon={<Icon icon={faCheck} />}
            data-testid="create-playlist-btn"
          />
        </Flex>
      )}
    </Box>
  );
};

export default CreatePlaylistBar;
